import {
  Divider,
  DocumentPreviewDrawer,
  Dropdown,
  Loader,
  Panel,
  Row,
  RowSize,
  ValidationWrapper,
} from "@/components";
import {
  DEFAULT_LOADING_TEXT,
  DEFAULT_SELECT_PLACEHOLDER_TEXT,
  DocTypeIds,
} from "@/constants";
import { DrawerParams } from "@/constants/drawer";
import { useRequestAttachmentsData } from "@/pages/requests/hooks/use-request-attachments-data";
import { Application, RequestResponse } from "@/services/api";
import { selectRequestFormStore, useBoundStore } from "@/services/stores";
import { selectApplicationStore } from "@/services/stores/selectors/applications";
import { map } from "lodash";
import { FC } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AttachmentRow } from "./components/attachment-row";
import { UploadRow } from "./components/upload-row";
import { ApplicationField } from "./constants";
import { useOperationsWithAttachments } from "./hooks/use-operations-with-attachments";
import { useRequiredDocuments } from "./hooks/use-required-documents";
import styles from "./styles.scss";
import { AttachmentField } from "./types";
import { InboxKeyParam } from "@/pages/inbox/types";

export const AttachmentsForm: FC<{
  data?: RequestResponse | null;
  onSetIsValidStep: (isValid: boolean) => void;
  onSetIsLockStepWithLoader: (isLocked: boolean) => void;
}> = ({ data, onSetIsValidStep, onSetIsLockStepWithLoader }) => {
  const [searchParams] = useSearchParams();

  const previewDocTypeId = searchParams.get(DrawerParams.DRAWER_DOC_ID);
  const referenceNumber = searchParams.get(InboxKeyParam.ReferenceNumber);

  const { isLoading, error } = useRequiredDocuments(
    referenceNumber || null,
    data,
  );

  const { applications, activeApplication, setActiveApplication } =
    useBoundStore(selectApplicationStore);

  const { ReferenceNumber } = useParams();

  const { isLoading: isLoadingEditedData, data: editedData } =
    useRequestAttachmentsData({
      requestId: data?.LOARequestBasicDetails?.Request_ID || ReferenceNumber,
      getFilesAsBase64: true,
    });

  const { requestFormData, resetRequestFormDataDocs } = useBoundStore(
    selectRequestFormStore,
  );

  const _editedData =
    data &&
    editedData &&
    activeApplication &&
    activeApplication?.RequiredDocuments
      ? [...activeApplication.RequiredDocuments, ...editedData]
      : activeApplication?.RequiredDocuments;

  const {
    onDownloadDocument,
    onUploadDocument,
    onRemoveDocument,
    onPreviewDocument,
  } = useOperationsWithAttachments({
    data: _editedData,
    onSetIsValidStep,
    onSetIsLockStepWithLoader,
  });

  const handleChangeApplication = (application: Application) => {
    setActiveApplication(application);

    if (activeApplication?.Id !== application.Id) {
      resetRequestFormDataDocs();
    }
  };

  const renderTable = () => {
    if (
      (!applications && isLoading) ||
      (ReferenceNumber && isLoadingEditedData)
    ) {
      return <Loader />;
    }

    if (error) {
      return (
        <div className={styles.error}>
          Can&apos;t loading required documents list
        </div>
      );
    }

    return map(activeApplication?.RequiredDocuments, (doc, index) => {
      const field = doc.DocNameEn as AttachmentField;
      const RequestDocuments = requestFormData?.RequestDocuments;

      const docTypeIdKey = doc.DocTypeId;

      const isContractor = doc.DocTypeId === DocTypeIds.Contractor;
      const isSubcontractor = doc.DocTypeId === DocTypeIds.Subcontractor;
      const isPersonList = doc.DocTypeId === DocTypeIds.PersonList;

      const isMandatory = isContractor || isSubcontractor || doc.IsMandatory;
      const removeDisabled = isPersonList || isSubcontractor || isContractor;

      return (
        <Row
          size={RowSize.Small}
          className={styles.panelRow}
          key={`doc-${index}`}
        >
          <div className={styles.panelRowTitle}>
            <span>
              {field}
              {isMandatory && <span className={styles.asterix}>*</span>}
            </span>
          </div>

          {docTypeIdKey && RequestDocuments?.[docTypeIdKey] ? (
            <AttachmentRow
              file={RequestDocuments[docTypeIdKey]}
              onDownloadDocument={() =>
                onDownloadDocument(RequestDocuments[docTypeIdKey])
              }
              onPreviewDocument={() => {
                onPreviewDocument(RequestDocuments[docTypeIdKey]);
              }}
              onRemoveAttachment={() => onRemoveDocument(`${docTypeIdKey}`)}
              removeDisabled={removeDisabled}
            />
          ) : (
            <UploadRow
              id={field}
              onUploadDocument={e => onUploadDocument(e, doc)}
              onDownloadDocument={onDownloadDocument}
              templateName={doc.TemplateTitle ?? ""}
              templateBase64={doc.TemplatePath}
              templateExtension={doc.Format}
            />
          )}
        </Row>
      );
    });
  };

  return (
    <>
      {applications && applications.length > 1 && (
        <>
          <Row size={RowSize.Small}>
            <ValidationWrapper>
              <Dropdown
                containerClassName={styles.dropdownContainer}
                id={ApplicationField.Application}
                label={ApplicationField.Application}
                placeholder={
                  isLoading
                    ? DEFAULT_LOADING_TEXT
                    : DEFAULT_SELECT_PLACEHOLDER_TEXT
                }
                // TODO Fix types
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                selectedOption={activeApplication}
                options={applications}
                isNeedToHighlightSelectedItem
                required={true}
                // TODO Fix types
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onChange={handleChangeApplication}
              />
            </ValidationWrapper>
          </Row>

          <Row size={RowSize.Small}>
            <Divider />
          </Row>
        </>
      )}

      <Row size={RowSize.Small}>
        <Panel
          header={"Upload documents"}
          panelInnerClassName={styles.panel}
          panelHeaderClassName={styles.panelHeader}
          dividerClassName={styles.panelDivider}
        >
          {renderTable()}
        </Panel>
      </Row>
      <div className={styles.spacer}></div>
      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <DocumentPreviewDrawer
        doc={
          previewDocTypeId
            ? (requestFormData?.RequestDocuments?.[previewDocTypeId]?.base64 as
                | string
                | null
                | undefined)
            : null
        }
      />
    </>
  );
};
